<div class="w-full max-w-6xl mx-auto p-6">
  @if (error()) {
    <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-2">
      <i class="pi pi-exclamation-triangle text-red-600"></i>
      <p class="text-red-800">{{ error() }}</p>
    </div>
  }

  @if (initialLoad()) {
    <p-card styleClass="sale-form-card">
      <ng-template pTemplate="header">
        <div class="px-4 py-3 border-b border-slate-200 bg-slate-50/50">
          <div class="flex items-center gap-3">
            <p-button
              icon="pi pi-arrow-left"
              [rounded]="true"
              [text]="true"
              severity="secondary"
              type="button"
              (onClick)="cancel()"
              pTooltip="Volver"
              tooltipPosition="bottom"
            />
            <h2 class="text-lg font-semibold text-slate-900 flex items-center gap-2 min-w-0">
              <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                <i class="pi pi-building"></i>
              </span>
              <span class="truncate">Cargando...</span>
            </h2>
          </div>
        </div>
      </ng-template>
      <div class="flex items-center justify-center py-12">
        <i class="pi pi-spin pi-spinner text-4xl text-slate-400"></i>
      </div>
    </p-card>
  } @else {
  <form [formGroup]="companyForm" (ngSubmit)="onSubmit()">
    <p-card styleClass="sale-form-card">
      <ng-template pTemplate="header">
        <div class="px-4 py-3 border-b border-slate-200 bg-slate-50/50">
          <div class="flex items-center gap-3">
            <p-button
              icon="pi pi-arrow-left"
              [rounded]="true"
              [text]="true"
              severity="secondary"
              type="button"
              (onClick)="cancel()"
              pTooltip="Volver"
              tooltipPosition="bottom"
            />
            <h2 class="text-lg font-semibold text-slate-900 flex items-center gap-2 min-w-0">
              <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                <i class="pi pi-building"></i>
              </span>
              <span class="truncate">{{ isEditMode() ? 'Editar empresa' : 'Configurar empresa' }}</span>
            </h2>
          </div>
        </div>
        <div class="flex flex-wrap items-center justify-end gap-2 border-b border-slate-200 bg-slate-50/90 px-3 py-2">
          <div class="inline-flex items-baseline gap-x-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
            <span class="text-[11px] font-semibold tracking-wider text-slate-500">STATUS:</span>
            <span class="text-sm font-medium text-slate-900 font-mono">{{ statusDisplayLabel() }}</span>
            <i
              class="pi pi-question-circle text-slate-400 cursor-help text-xs shrink-0"
              [pTooltip]="statusTooltip"
              tooltipPosition="bottom"
            ></i>
          </div>
        </div>
      </ng-template>
      <p-tabs [value]="activeTab()" (valueChange)="activeTab.set($event)" class="">
        <p-tablist>
          @for (tab of tabs; track tab.value) {
            <p-tab
              [value]="tab.value"
              [disabled]="tab.value !== 'generales' && tab.value !== 'terminos-pago' && !editingCompanyIdValue()"
              class="flex items-center gap-2! text-inherit"
            >
              <i [class]="tab.icon"></i>
              <span>{{ tab.label }}</span>
            </p-tab>
          }
        </p-tablist>
        <p-tabpanels>
          <p-tabpanel value="generales">
            <div class="mt-4 mb-4">
              <h3 class="text-lg font-semibold text-slate-900">Información Básica</h3>
              <p class="text-sm text-slate-500">Datos fiscales y de contacto de la empresa.</p>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4">
              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <p-select
                    formControlName="type"
                    inputId="type"
                    [options]="companyTypes"
                    optionLabel="label"
                    optionValue="value"
                    class="w-full"
                    [invalid]="(companyForm.get('type')?.invalid ?? false) && (companyForm.get('type')?.touched ?? false)"
                  />
                  <label for="type">Tipo</label>
                </p-floatlabel>
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <p-select 
                    formControlName="taxRegimeId"
                    inputId="taxRegime"
                    [options]="taxRegimeOptions()"
                    optionLabel="description"
                    optionValue="id"
                    [filter]="true"
                    filterPlaceholder="Buscar..."
                    class="w-full"
                    [invalid]="(companyForm.get('taxRegimeId')?.invalid ?? false) && (companyForm.get('taxRegimeId')?.touched ?? false)"
                  />
                  <label for="taxRegime">Régimen Fiscal</label>
                </p-floatlabel>
                @if (companyForm.get('taxRegimeId')?.hasError('required') && companyForm.get('taxRegimeId')?.touched) {
                  <small class="text-red-600">El régimen fiscal es requerido</small>
                }
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel variant="on">
                  <input pInputText
                    id="taxId"
                    formControlName="taxId"
                    [required]="true"
                    [maxlength]="50"
                    fluid
                    autocomplete="off"
                    [invalid]="(companyForm.get('taxId')?.invalid ?? false) && (companyForm.get('taxId')?.touched ?? false)"
                  />
                  <label for="taxId">RFC / Tax ID</label>
                </p-floatlabel>
                @if (companyForm.get('taxId')?.hasError('required') && companyForm.get('taxId')?.touched) {
                  <small class="text-red-600">El RFC es requerido</small>
                }
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel variant="on">
                  <input pInputText
                    id="legalName"
                    formControlName="legalName"
                    [maxlength]="200"
                    fluid
                    autocomplete="off"
                    [invalid]="(companyForm.get('legalName')?.invalid ?? false) && (companyForm.get('legalName')?.touched ?? false)"
                  />
                  <label for="legalName">Razón Social</label>
                </p-floatlabel>
                @if (companyForm.get('legalName')?.hasError('required') && companyForm.get('legalName')?.touched) {
                  <small class="text-red-600">La razón social es requerida</small>
                }
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel variant="on">
                  <input pInputText
                    id="comercialName"
                    formControlName="comercialName"
                    [maxlength]="200"
                    fluid
                    autocomplete="off"
                    [invalid]="(companyForm.get('comercialName')?.invalid ?? false) && (companyForm.get('comercialName')?.touched ?? false)"
                  />
                  <label for="comercialName">Nombre Comercial</label>
                </p-floatlabel>
                @if (companyForm.get('comercialName')?.hasError('required') && companyForm.get('comercialName')?.touched) {
                  <small class="text-red-600">El nombre comercial es requerido</small>
                }
              </div>

              @if (companyForm.get('type')?.value === CompanyTypeEnum.PERSON) {
                <div class="flex flex-col gap-1">
                  <p-floatlabel variant="on">
                    <input pInputText id="firstName" formControlName="firstName" [maxlength]="150" fluid autocomplete="off"
                      [invalid]="(companyForm.get('firstName')?.invalid ?? false) && (companyForm.get('firstName')?.touched ?? false)" />
                    <label for="firstName">Primer Nombre</label>
                  </p-floatlabel>
                  @if (companyForm.get('firstName')?.hasError('required') && companyForm.get('firstName')?.touched) {
                    <small class="text-red-600">Requerido</small>
                  }
                </div>
                <div class="flex flex-col gap-1">
                  <p-floatlabel variant="on">
                    <input pInputText id="lastName" formControlName="lastName" [maxlength]="150" fluid autocomplete="off"
                      [invalid]="(companyForm.get('lastName')?.invalid ?? false) && (companyForm.get('lastName')?.touched ?? false)" />
                    <label for="lastName">Apellido</label>
                  </p-floatlabel>
                  @if (companyForm.get('lastName')?.hasError('required') && companyForm.get('lastName')?.touched) {
                    <small class="text-red-600">Requerido</small>
                  }
                </div>
              }

              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <p-select
                    formControlName="countryCode"
                    inputId="countryCode"
                    [options]="countries()"
                    optionLabel="name"
                    optionValue="code"
                    placeholder="Seleccionar país"
                    class="w-full"
                    [invalid]="(companyForm.get('countryCode')?.invalid ?? false) && (companyForm.get('countryCode')?.touched ?? false)"
                  />
                  <label for="countryCode">País</label>
                </p-floatlabel>
                @if (companyForm.get('countryCode')?.hasError('required') && companyForm.get('countryCode')?.touched) {
                  <small class="text-red-600">El país es requerido</small>
                }
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel variant="on">
                  <input pInputText id="email" type="email" formControlName="email" [maxlength]="100" fluid autocomplete="off"
                    [invalid]="(companyForm.get('email')?.invalid ?? false) && (companyForm.get('email')?.touched ?? false)" />
                  <label for="email">Email</label>
                </p-floatlabel>
                @if (companyForm.get('email')?.hasError('email') && companyForm.get('email')?.touched) {
                  <small class="text-red-600">Email inválido</small>
                }
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel variant="on">
                  <input pInputText id="phone" formControlName="phone" [maxlength]="20" fluid autocomplete="off"
                    pInputMask="(999) 999-9999" placeholder="(999) 999-9999"
                    [invalid]="(companyForm.get('phone')?.invalid ?? false) && (companyForm.get('phone')?.touched ?? false)" />
                  <label for="phone">Teléfono</label>
                </p-floatlabel>
              </div>

              <!-- Logo de la empresa -->
              <div class="md:col-span-2 mt-2">
                <div class="mb-2">
                  <h4 class="text-sm font-semibold text-slate-700">Logo de la Empresa</h4>
                  <p class="text-xs text-slate-500 mt-0.5">Sube el logo de tu empresa (máximo 1 MB).</p>
                </div>
                <div class="flex items-center gap-6">
                  <!-- Preview -->
                  <div class="w-24 h-24 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden flex-shrink-0 bg-slate-50">
                    @if (logoPreviewUrl()) {
                      <img [src]="logoPreviewUrl()" alt="Logo" class="max-w-full max-h-full object-contain" />
                    } @else {
                      <i class="pi pi-image text-3xl text-slate-300"></i>
                    }
                  </div>
                  <!-- Acciones -->
                  <div class="flex flex-col gap-2">
                    <label class="cursor-pointer">
                      <input type="file" class="hidden" accept="image/*" (change)="onLogoFileSelect($event)" />
                      <span class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                        <i class="pi pi-upload text-xs"></i>
                        {{ logoPreviewUrl() ? 'Cambiar Logo' : 'Subir Logo' }}
                      </span>
                    </label>
                    @if (logoPreviewUrl()) {
                      <button type="button"
                        class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        (click)="removeLogo()">
                        <i class="pi pi-trash text-xs"></i>
                        Eliminar
                      </button>
                    }
                  </div>
                </div>
              </div>
            </div>
          </p-tabpanel>

          <p-tabpanel value="terminos-pago">
            <div class="flex items-center justify-between mt-4 mb-4">
              <div>
                <h3 class="text-lg font-semibold text-slate-900">Términos de pago</h3>
                <p class="text-sm text-slate-500">Plazo de pago aplicable por defecto a la empresa.</p>
              </div>
            </div>
            <div class="flex gap-3 p-4 rounded-lg bg-slate-50 border border-slate-200 mb-6">
              <i class="pi pi-info-circle text-sky-600 mt-0.5 shrink-0" aria-hidden="true"></i>
              <div class="text-sm text-slate-700">
                <p class="font-medium text-slate-800 mb-1">¿Para qué sirve esta sección?</p>
                <p>
                  La configuración de términos de pago de la empresa es la <strong>predeterminada para todas las ventas</strong>.
                  Si un cliente tiene configurados términos de pago específicos en su ficha, se aplicarán esos en lugar de los de la empresa.
                </p>
              </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4">
              <div class="flex flex-col gap-1">
                <p-floatlabel class="w-full" variant="on">
                  <p-inputNumber
                    inputId="daysUntilExpiry"
                    formControlName="daysUntilExpiry"
                    [min]="0"
                    [useGrouping]="false"
                    [minFractionDigits]="0"
                    [maxFractionDigits]="0"
                    fluid
                    placeholder="0"
                    [invalid]="(companyForm.get('daysUntilExpiry')?.invalid ?? false) && (companyForm.get('daysUntilExpiry')?.touched ?? false)"
                  />
                  <label for="daysUntilExpiry">Plazo de pago (días)</label>
                </p-floatlabel>
                @if (companyForm.get('daysUntilExpiry')?.hasError('nonNegativeInteger') && companyForm.get('daysUntilExpiry')?.touched) {
                  <small class="text-red-600">Indica un entero mayor o igual a 0.</small>
                }
              </div>
            </div>
          </p-tabpanel>

          <p-tabpanel value="direcciones">
            @if (!editingCompanyIdValue()) {
              <p class="text-slate-500 py-6">Guarda la empresa para habilitar direcciones.</p>
            } @else {
              <div class="flex items-center justify-between mt-4 mb-4">
                <div>
                  <h3 class="text-lg font-semibold text-slate-900">Direcciones</h3>
                  <p class="text-sm text-slate-500">Agrega o edita direcciones fiscales y de contacto.</p>
                </div>
                <p-button label="Nueva" icon="pi pi-plus" (onClick)="newAddress()" />
              </div>
              <div class="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <div class="overflow-x-auto">
                  <table class="w-full">
                    <thead class="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Tipo</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Dirección</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Municipio / Estado / CP</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">País</th>
                        <th class="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-slate-200">
                      @if (addresses().length === 0) {
                        <tr>
                          <td class="px-6 py-6 text-center text-sm text-slate-500" colspan="5">No hay direcciones registradas.</td>
                        </tr>
                      }
                      @for (address of addresses(); track address.id) {
                        <tr class="hover:bg-slate-50 transition-colors">
                          <td class="px-6 py-4 whitespace-nowrap">
                            <span class="flex items-center gap-2 text-sm font-medium text-slate-900">
                              <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-100" style="color: var(--p-primary-500)">
                                <i [class]="'pi ' + getAddressTypeIcon(address.type)"></i>
                              </span>
                              {{ getAddressTypeLabel(address.type) }}
                            </span>
                          </td>
                          <td class="px-6 py-4">
                            <div class="text-sm text-slate-900">{{ formatAddressStreet(address) }}</div>
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                            {{ formatAddressLocality(address) }}
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{{ address.country?.name ?? address.country?.code ?? '-' }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-right">
                            <p-button icon="pi pi-pen-to-square" [rounded]="true" [text]="true" severity="secondary" styleClass="hover:!bg-sky-100" (onClick)="startEditAddress(address)" />
                            <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger" (onClick)="openDeleteAddress(address)" />
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            }
          </p-tabpanel>

          <p-tabpanel value="contactos">
            @if (!editingCompanyIdValue()) {
              <p class="text-slate-500 py-6">Guarda la empresa para habilitar contactos.</p>
            } @else {
              <div class="flex items-center justify-between mt-4 mb-4">
                <div>
                  <h3 class="text-lg font-semibold text-slate-900">Contactos</h3>
                  <p class="text-sm text-slate-500">Agrega o edita contactos de la empresa.</p>
                </div>
                <p-button label="Nuevo" icon="pi pi-plus" (onClick)="newContact()" />
              </div>
              <div class="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <div class="overflow-x-auto">
                  <table class="w-full">
                    <thead class="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Tipo</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Nombre</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Email</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Teléfono</th>
                        <th class="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-slate-200">
                      @if (contacts().length === 0) {
                        <tr>
                          <td class="px-6 py-6 text-center text-sm text-slate-500" colspan="5">No hay contactos registrados.</td>
                        </tr>
                      }
                      @for (contact of contacts(); track contact.id) {
                        <tr class="hover:bg-slate-50 transition-colors">
                          <td class="px-6 py-4 whitespace-nowrap">
                            <span class="flex items-center gap-2 text-sm font-medium text-slate-900">
                              <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-100" style="color: var(--p-primary-500)">
                                <i [class]="'pi ' + getContactTypeIcon(contact.type)"></i>
                              </span>
                              {{ getContactTypeLabel(contact.type) }}
                            </span>
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{{ contact.conactName }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{{ contact.email || '-' }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{{ contact.phone || '-' }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-right">
                            <p-button icon="pi pi-pen-to-square" [rounded]="true" [text]="true" severity="secondary" styleClass="hover:!bg-sky-100" (onClick)="startEditContact(contact)" />
                            <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger" (onClick)="openDeleteContact(contact)" />
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            }
          </p-tabpanel>

          <p-tabpanel value="firmas">
            <app-company-email-signatures
              [enabled]="!!editingCompanyIdValue()"
              [companyId]="editingCompanyIdValue()"
              [companyName]="companyForm.get('comercialName')?.value || companyForm.get('legalName')?.value || ''"
              [companyEmail]="companyForm.get('email')?.value || ''"
              [companyPhone]="companyForm.get('phone')?.value || ''"
              [logoUrl]="logoPreviewUrl()"
            />
          </p-tabpanel>

          <p-tabpanel value="series">
            @if (!editingCompanyIdValue()) {
              <p class="text-slate-500 py-6">Guarda la empresa para habilitar series.</p>
            } @else {
              <div class="flex items-center justify-between mt-4 mb-4">
                <div>
                  <h3 class="text-lg font-semibold text-slate-900">Series</h3>
                  <p class="text-sm text-slate-500">Gestiona las series para folios y numeración.</p>
                </div>
                <p-button label="Nueva" icon="pi pi-plus" (onClick)="newSerie()" appDisableUnless="SERIE_WRITE" />
              </div>
              <div class="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <div class="overflow-x-auto">
                  <table class="w-full">
                    <thead class="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Serie</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Tipo</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider hidden sm:table-cell">Valor / Padding</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Estado</th>
                        <th class="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-slate-200">
                      @if (seriesLoading()) {
                        <tr>
                          <td class="px-6 py-8 text-center text-sm text-slate-500" colspan="5">
                            <i class="pi pi-spin pi-spinner mr-2"></i>Cargando series...
                          </td>
                        </tr>
                      } @else if (series().length === 0) {
                        <tr>
                          <td class="px-6 py-6 text-center text-sm text-slate-500" colspan="5">No hay series registradas.</td>
                        </tr>
                      } @else {
                        @for (serie of series(); track serie.id) {
                          <tr class="hover:bg-slate-50 transition-colors">
                            <td class="px-6 py-4 whitespace-nowrap">
                              <div class="flex items-center gap-3">
                                <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700 font-semibold text-sm">
                                  {{ serie.serie?.trim() ? serie.serie : '—' }}
                                </span>
                                <div class="min-w-0">
                                  <div class="text-sm font-medium text-slate-900 truncate">{{ serieLabel(serie) }}</div>
                                  <div class="text-xs text-slate-500 truncate">{{ serie.description || 'Sin descripción' }}</div>
                                </div>
                              </div>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                              <p-tag class="!text-xs" [value]="typeLabel(serie.type)" [severity]="getTypeSeverity(serie.type)" />
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600 hidden sm:table-cell">
                              Inicio {{ serie.sequence ?? '-' }} · Padding {{ serie.padding }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                              <p-tag
                                class="!text-xs"
                                [value]="serie.state === 'ACTIVE' ? 'Activo' : 'Inactivo'"
                                [severity]="getSerieStateSeverity(serie.state)" />
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-right">
                              <p-button icon="pi pi-pen-to-square" [rounded]="true" [text]="true" severity="secondary" styleClass="hover:!bg-sky-100" (onClick)="startEditSerie(serie)" />
                              <p-button
                                icon="pi pi-trash"
                                [rounded]="true"
                                [text]="true"
                                severity="danger"
                                [disabled]="serie.state === 'INACTIVE'"
                                (onClick)="openDeleteSerie(serie)" />
                            </td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            }
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>

      <ng-template #footer>
        <div class="!mt-4 flex justify-end gap-4 mt-1 !p-4 !-m-5 bg-gray-50 p-2 border-t border-slate-200">
          <p-button label="Cancelar" severity="secondary" (onClick)="cancel()" [loading]="loading()" type="button" />
          <p-button
            [label]="loading() ? 'Guardando...' : (isEditMode() ? 'Actualizar Empresa' : 'Guardar Empresa')"
            [loading]="loading()"
            [disabled]="companyForm.invalid || loading()"
            type="submit"
            appDisableUnless="COMPANY_WRITE"
          />
        </div>
      </ng-template>
    </p-card>
  </form>
  }

  <!-- Modal Dirección -->
  @if (showAddressForm()) {
    <div class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-black transition-opacity duration-200" [class.bg-opacity-40]="addressModalShown()" [class.bg-opacity-0]="!addressModalShown()" (click)="cancelAddressEdit()"></div>
      <div class="relative bg-white rounded-xl shadow-xl w-full max-w-lg p-6 z-10 transform transition-all duration-200"
        [class.opacity-100]="addressModalShown()" [class.opacity-0]="!addressModalShown()" [class.scale-100]="addressModalShown()" [class.scale-95]="!addressModalShown()">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-slate-900">{{ editingAddressId() ? 'Editar dirección' : 'Nueva dirección' }}</h3>
          <p-button icon="pi pi-times" [rounded]="true" [text]="true" severity="secondary" (onClick)="cancelAddressEdit()" />
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4" [formGroup]="addressForm">
          <!-- Tipo 50% -->
          <div class="flex flex-col gap-1 md:col-span-2 md:w-1/2">
            <p-floatlabel class="w-full" variant="on">
              <p-select formControlName="addressType" inputId="addressType" [options]="addressTypes" optionLabel="label" optionValue="value" class="w-full"
                [invalid]="(addressForm.get('addressType')?.invalid ?? false) && (addressForm.get('addressType')?.touched ?? false)" />
              <label for="addressType">Tipo <span class="text-red-500">*</span></label>
            </p-floatlabel>
            @if (addressForm.get('addressType')?.hasError('required') && addressForm.get('addressType')?.touched) {
              <small class="text-red-600">Campo obligatorio</small>
            }
          </div>

          <!-- Calle 100% -->
          <div class="flex flex-col gap-1 md:col-span-2">
            <p-floatlabel variant="on">
              <input pInputText id="addressStreet" formControlName="street" [maxlength]="maxLength.street" fluid autocomplete="off"
                [invalid]="(addressForm.get('street')?.invalid ?? false) && (addressForm.get('street')?.touched ?? false)" />
              <label for="addressStreet">Calle <span class="text-red-500">*</span></label>
            </p-floatlabel>
            @if (addressForm.get('street')?.hasError('required') && addressForm.get('street')?.touched) {
              <small class="text-red-600">Campo obligatorio</small>
            }
            @if (addressForm.get('street')?.hasError('maxlength')) {
              <small class="text-red-600">Máximo {{ maxLength.street }} caracteres</small>
            }
          </div>

          <!-- No. Ext 50% -->
          <div class="flex flex-col gap-1">
            <p-floatlabel variant="on">
              <input pInputText id="addressExternalNo" formControlName="externalNo" [maxlength]="maxLength.externalNo" fluid autocomplete="off"
                [invalid]="(addressForm.get('externalNo')?.invalid ?? false) && (addressForm.get('externalNo')?.touched ?? false)" />
              <label for="addressExternalNo">No. Ext <span class="text-red-500">*</span></label>
            </p-floatlabel>
            @if (addressForm.get('externalNo')?.hasError('required') && addressForm.get('externalNo')?.touched) {
              <small class="text-red-600">Campo obligatorio</small>
            }
            @if (addressForm.get('externalNo')?.hasError('maxlength')) {
              <small class="text-red-600">Máximo {{ maxLength.externalNo }} caracteres</small>
            }
          </div>

          <!-- No. Int 50% -->
          <div class="flex flex-col gap-1">
            <p-floatlabel variant="on">
              <input pInputText id="addressInternalNo" formControlName="internalNo" [maxlength]="maxLength.internalNo" fluid autocomplete="off"
                [invalid]="(addressForm.get('internalNo')?.invalid ?? false) && (addressForm.get('internalNo')?.touched ?? false)" />
              <label for="addressInternalNo">No. Int</label>
            </p-floatlabel>
            @if (addressForm.get('internalNo')?.hasError('maxlength')) {
              <small class="text-red-600">Máximo {{ maxLength.internalNo }} caracteres</small>
            }
          </div>

          <!-- CP 50% -->
          <div class="flex flex-col gap-1">
            <p-floatlabel variant="on">
              <input pInputText id="addressZipCode" formControlName="zipCode" [maxlength]="maxLength.zipCode" fluid type="tel" inputmode="numeric" autocomplete="off"
                (input)="onZipCodeInput($event)" (blur)="onZipCodeBlur()"
                [invalid]="(addressForm.get('zipCode')?.invalid ?? false) && (addressForm.get('zipCode')?.touched ?? false)" />
              <label for="addressZipCode">CP <span class="text-red-500">*</span></label>
            </p-floatlabel>
            @if (postalCodeLoading()) {
              <small class="text-slate-500">Consultando código postal...</small>
            }
            @if (addressForm.get('zipCode')?.hasError('required') && addressForm.get('zipCode')?.touched) {
              <small class="text-red-600">Campo obligatorio</small>
            }
            @if (addressForm.get('zipCode')?.hasError('pattern') && addressForm.get('zipCode')?.touched && !addressForm.get('zipCode')?.hasError('required')) {
              <small class="text-red-600">Debe tener exactamente {{ maxLength.zipCode }} dígitos</small>
            }
          </div>

          <!-- Colonia 50% -->
          <div class="flex flex-col gap-1">
            @if (isMexicoSelected()) {
              <p-floatlabel class="w-full" variant="on">
                <p-select formControlName="colonia" inputId="addressColonia" [options]="coloniaOptions()" optionLabel="label" optionValue="value"
                  [filter]="true" filterPlaceholder="Buscar colonia..." class="w-full"
                  [invalid]="(addressForm.get('colonia')?.invalid ?? false) && (addressForm.get('colonia')?.touched ?? false)" />
                <label for="addressColonia">Colonia <span class="text-red-500">*</span></label>
              </p-floatlabel>
            } @else {
              <p-floatlabel variant="on">
                <input pInputText id="addressColonia" formControlName="colonia" [maxlength]="maxLength.colonia" fluid autocomplete="off"
                  [invalid]="(addressForm.get('colonia')?.invalid ?? false) && (addressForm.get('colonia')?.touched ?? false)" />
                <label for="addressColonia">Colonia <span class="text-red-500">*</span></label>
              </p-floatlabel>
            }
            @if (addressForm.get('colonia')?.hasError('required') && addressForm.get('colonia')?.touched) {
              <small class="text-red-600">Campo obligatorio</small>
            }
            @if (addressForm.get('colonia')?.hasError('maxlength')) {
              <small class="text-red-600">Máximo {{ maxLength.colonia }} caracteres</small>
            }
          </div>

          <!-- Municipio auto 50% -->
          <div class="flex flex-col gap-1">
            <p-floatlabel variant="on">
              <input pInputText id="addressMunicipality" [value]="municipalityDisplay()" [readonly]="true" fluid autocomplete="off"
                [invalid]="(addressForm.get('municipality')?.invalid ?? false) && ((addressForm.get('municipality')?.touched ?? false) || (addressForm.get('zipCode')?.touched ?? false))" />
              <label for="addressMunicipality">Municipio <span class="text-red-500">*</span></label>
            </p-floatlabel>
            @if (addressForm.get('municipality')?.hasError('required') && (addressForm.get('municipality')?.touched || addressForm.get('zipCode')?.touched)) {
              <small class="text-red-600">Consulta un CP válido para autocompletar</small>
            }
          </div>

          <!-- País 50% (México por defecto) -->
          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-select formControlName="addressCountry" inputId="addressCountry" [options]="countries()" optionLabel="name" optionValue="code" class="w-full"
                [invalid]="(addressForm.get('addressCountry')?.invalid ?? false) && (addressForm.get('addressCountry')?.touched ?? false)" />
              <label for="addressCountry">País <span class="text-red-500">*</span></label>
            </p-floatlabel>
            @if (addressForm.get('addressCountry')?.hasError('required') && addressForm.get('addressCountry')?.touched) {
              <small class="text-red-600">Campo obligatorio</small>
            }
          </div>
        </div>
        <div class="flex justify-end gap-3 mt-6">
          <p-button label="Cancelar" severity="secondary" (onClick)="cancelAddressEdit()" />
          <p-button [label]="relationsLoading() ? 'Guardando...' : (editingAddressId() ? 'Actualizar' : 'Guardar')" icon="pi pi-check" [loading]="relationsLoading()" (onClick)="saveAddress()" />
        </div>
      </div>
    </div>
  }

  <!-- Modal Contacto -->
  @if (showContactForm()) {
    <div class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-black transition-opacity duration-200" [class.bg-opacity-40]="contactModalShown()" [class.bg-opacity-0]="!contactModalShown()" (click)="cancelContactEdit()"></div>
      <div class="relative bg-white rounded-xl shadow-xl w-full max-w-lg p-6 z-10 transform transition-all duration-200"
        [class.opacity-100]="contactModalShown()" [class.opacity-0]="!contactModalShown()" [class.scale-100]="contactModalShown()" [class.scale-95]="!contactModalShown()">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-slate-900">{{ editingContactId() ? 'Editar contacto' : 'Nuevo contacto' }}</h3>
          <p-button icon="pi pi-times" [rounded]="true" [text]="true" severity="secondary" (onClick)="cancelContactEdit()" />
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4" [formGroup]="contactForm">
          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-select formControlName="contactType" inputId="contactType" [options]="contactTypes" optionLabel="label" optionValue="value" class="w-full" />
              <label for="contactType">Tipo</label>
            </p-floatlabel>
          </div>
          <div class="flex flex-col gap-1 col-span-2">
            <p-floatlabel variant="on">
              <input pInputText id="contactName" formControlName="conactName" [maxlength]="100" fluid autocomplete="off"
                [invalid]="(contactForm.get('conactName')?.invalid ?? false) && (contactForm.get('conactName')?.touched ?? false)" />
              <label for="contactName">Nombre del Contacto</label>
            </p-floatlabel>
          </div>
          <div class="flex flex-col gap-1">
            <p-floatlabel variant="on">
              <input pInputText id="contactEmail" type="email" formControlName="contactEmail" [maxlength]="100" fluid autocomplete="off" />
              <label for="contactEmail">Email</label>
            </p-floatlabel>
          </div>
          <div class="flex flex-col gap-1">
            <p-floatlabel variant="on">
              <input pInputText id="contactPhone" formControlName="contactPhone" fluid autocomplete="off" pInputMask="(999) 999-9999" placeholder="(999) 999-9999" [maxlength]="14" />
              <label for="contactPhone">Teléfono</label>
            </p-floatlabel>
          </div>
        </div>
        <div class="flex justify-end gap-3 mt-6">
          <p-button label="Cancelar" severity="secondary" (onClick)="cancelContactEdit()" />
          <p-button [label]="relationsLoading() ? 'Guardando...' : (editingContactId() ? 'Actualizar' : 'Guardar')" icon="pi pi-check" [loading]="relationsLoading()" (onClick)="saveContact()" />
        </div>
      </div>
    </div>
  }

  <!-- Modal Serie -->
  @if (showSerieForm()) {
    <div class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black transition-opacity duration-200" [class.bg-opacity-40]="serieModalShown()" [class.bg-opacity-0]="!serieModalShown()" (click)="cancelSerieEdit()"></div>
      <div class="relative bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 z-10 transform transition-all duration-200 max-h-[90vh] overflow-y-auto"
        [class.opacity-100]="serieModalShown()" [class.opacity-0]="!serieModalShown()" [class.scale-100]="serieModalShown()" [class.scale-95]="!serieModalShown()">
        <div class="flex items-center justify-between mb-2">
          <div>
            <h3 class="text-lg font-semibold text-slate-900">{{ editingSerieId() ? 'Editar serie' : 'Nueva serie' }}</h3>
            <p class="text-sm text-slate-500 mt-1">
              {{ editingSerieId() ? 'Actualiza la configuración de la serie' : 'Completa el formulario para crear una nueva serie' }}
            </p>
          </div>
          <p-button icon="pi pi-times" [rounded]="true" [text]="true" severity="secondary" (onClick)="cancelSerieEdit()" />
        </div>

        <div class="mt-4 mb-4">
          <h4 class="text-base font-semibold text-slate-900">Información de la serie</h4>
          <p class="text-sm text-slate-500">Código, descripción y configuración de numeración.</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6" [formGroup]="serieForm">
          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-select
                formControlName="type"
                inputId="serieType"
                [options]="serieTypeOptions"
                optionLabel="label"
                optionValue="value"
                class="w-full"
                [invalid]="(serieForm.get('type')?.invalid ?? false) && (serieForm.get('type')?.touched ?? false)"
              />
              <label for="serieType">Tipo <span class="text-red-500">*</span></label>
            </p-floatlabel>
            <p class="text-xs text-slate-500">Indica a qué tipo de documento corresponde la serie.</p>
            @if (serieForm.get('type')?.hasError('required') && serieForm.get('type')?.touched) {
              <small class="text-red-600">El tipo es requerido</small>
            }
          </div>

          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <input
                pInputText
                id="serieCode"
                formControlName="serie"
                [maxlength]="5"
                fluid
                autocomplete="off"
                [invalid]="(serieForm.get('serie')?.invalid ?? false) && (serieForm.get('serie')?.touched ?? false)"
              />
              <label for="serieCode">Serie (máx. 5 caracteres)</label>
            </p-floatlabel>
            <p class="text-xs text-slate-500">Déjalo vacío para crear una secuencia sin serie.</p>
            @if (serieForm.get('serie')?.hasError('maxlength')) {
              <small class="text-red-600">Máximo 5 caracteres</small>
            }
          </div>

          <div class="flex flex-col gap-1 md:col-span-2">
            <p-floatlabel class="w-full" variant="on">
              <input
                pInputText
                id="serieDescription"
                formControlName="description"
                [maxlength]="500"
                fluid
                autocomplete="off"
              />
              <label for="serieDescription">Descripción</label>
            </p-floatlabel>
            <p class="text-xs text-slate-500">Describe el uso de la serie.</p>
            @if (serieForm.get('description')?.hasError('maxlength')) {
              <small class="text-red-600">Máximo 500 caracteres</small>
            }
          </div>

          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <input
                pInputText
                type="number"
                id="serieSequence"
                formControlName="sequence"
                min="0"
                fluid
                autocomplete="off"
                [readonly]="!!editingSerieId()"
              />
              <label for="serieSequence">{{ editingSerieId() ? 'Valor actual' : 'Valor inicial' }}</label>
            </p-floatlabel>
            @if (editingSerieId()) {
              <p class="text-xs text-slate-500">El valor inicial solo se puede definir al crear la serie.</p>
            }
          </div>

          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <input
                pInputText
                type="number"
                id="seriePadding"
                formControlName="padding"
                min="0"
                fluid
                autocomplete="off"
                [invalid]="(serieForm.get('padding')?.invalid ?? false) && (serieForm.get('padding')?.touched ?? false)"
              />
              <label for="seriePadding">Ceros a la izquierda (padding) <span class="text-red-500">*</span></label>
            </p-floatlabel>
            <p class="text-xs text-slate-500">Ej: 6 → 000001, 000002...</p>
            @if (serieForm.get('padding')?.hasError('min') && serieForm.get('padding')?.touched) {
              <small class="text-red-600">Debe ser mayor o igual a 0</small>
            }
          </div>
        </div>

        <div class="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-200">
          <p-button label="Cancelar" severity="secondary" (onClick)="cancelSerieEdit()" [disabled]="relationsLoading()" />
          <p-button
            [label]="relationsLoading() ? 'Guardando...' : (editingSerieId() ? 'Guardar cambios' : 'Crear serie')"
            icon="pi pi-check"
            [loading]="relationsLoading()"
            [disabled]="serieForm.invalid || relationsLoading()"
            (onClick)="saveSerie()" />
        </div>
      </div>
    </div>
  }

  <!-- Modal confirmar eliminar -->
  @if (deleteConfirmOpen()) {
    <div class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-black transition-opacity duration-200" [class.bg-opacity-40]="deleteConfirmShown()" [class.bg-opacity-0]="!deleteConfirmShown()" (click)="closeDeleteConfirm()"></div>
      <div class="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6 z-10 transform transition-all duration-200"
        [class.opacity-100]="deleteConfirmShown()" [class.opacity-0]="!deleteConfirmShown()" [class.scale-100]="deleteConfirmShown()" [class.scale-95]="!deleteConfirmShown()">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-slate-900">
            @if (deleteTarget() === 'address') { Eliminar dirección }
            @if (deleteTarget() === 'contact') { Eliminar contacto }
            @if (deleteTarget() === 'serie') { Eliminar serie }
          </h3>
          <p-button icon="pi pi-times" [rounded]="true" [text]="true" severity="secondary" (onClick)="closeDeleteConfirm()" />
        </div>
        <p class="text-sm text-slate-600 mb-6">
          @if (deleteTarget() === 'address' && deleteAddressItem(); as addr) {
            ¿Eliminar la dirección {{ getAddressTypeLabel(addr.type) }} – {{ formatAddressStreet(addr) }}?
          }
          @if (deleteTarget() === 'contact' && deleteContactItem(); as con) {
            ¿Eliminar el contacto {{ con.conactName }} ({{ getContactTypeLabel(con.type) }})?
          }
          @if (deleteTarget() === 'serie' && deleteSerieItem(); as ser) {
            ¿Eliminar la serie "{{ serieLabel(ser) }}" ({{ typeLabel(ser.type) }})? La serie se marcará como inactiva.
          }
        </p>
        <div class="flex justify-end gap-3">
          <p-button label="Cancelar" severity="secondary" (onClick)="closeDeleteConfirm()" [disabled]="deleteLoading()" />
          <p-button label="Eliminar" severity="danger" icon="pi pi-trash" [loading]="deleteLoading()" [disabled]="deleteLoading()" (onClick)="confirmDelete()" />
        </div>
      </div>
    </div>
  }
</div>
